import { FormHints, HintProps } from '../FormHints/FormHints.component';
import React, {
  ComponentPropsWithRef,
  FC,
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SecondaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { Text } from '@components/Text/Text.component';
import { TextProps } from '@components/Text/Text.types';
import { FormElement, Value } from '@form/business/form.types';
import { Checkbox } from '@form/components/Checkbox/Checkbox.component';
import { Label, LabelProps } from '@form/components/Label/Label.component';
import { ChevronDownIcon } from '@icons/ChevronDown.icon';
import classnames from 'classnames';
import styles from './AdvancedSelect.module.scss';

export type AdvancedSelectProps = Omit<ComponentPropsWithRef<'div'>, 'onSelect'> &
  LabelProps &
  HintProps &
  FormElement & {
    options: Value[];
    value?: Value | Value[];
    onSelect: (values?: Value | Value[]) => void;
    multiple?: boolean;
    required?: boolean;
    labelPosition?: 'top' | 'inner';
    selectedOptionFlavour?: TextProps['flavour'];
    placeHolderI18n?: string;
    disabled?: boolean;
    hideArrow?: boolean;
    isJournalPage?: boolean;
  };

function isOptionChecked(option: Value, value: AdvancedSelectProps['value']) {
  if (!value) {
    return false;
  }
  return Array.isArray(value) ? value.some((v) => v.value === option.value) : value.value === option.value;
}

export const AdvancedSelect: FC<AdvancedSelectProps> = forwardRef(
  (
    {
      options,
      label,
      labelI18n,
      value,
      onSelect,
      multiple = false,
      required,
      labelPosition = 'inner',
      labelFlavour = 'dark',
      selectedOptionFlavour = 'dark',
      placeHolderI18n,
      disabled,
      isHintVisible,
      validators,
      className,
      variant,
      hideArrow,
      isJournalPage = false,
      ...rest
    },
    advancedSelectRef
  ) => {
    const currentValue = value ? (Array.isArray(value) ? value : [value]) : undefined;
    const [isOpen, setOpen] = useState(false);

    const node = useRef<HTMLDivElement>(null);
    const handleClickOutside = useCallback(
      (e: MouseEvent) => {
        if (node.current && node.current.contains(e.target as Node)) {
          return;
        }
        setOpen(false);
      },
      [node, setOpen]
    );
    useEffect(() => {
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, handleClickOutside]);

    const onSelection = (option: Value) => {
      if (multiple) {
        if (isOptionChecked(option, value)) {
          onSelect(currentValue?.filter((element) => element.value !== option.value));
        } else {
          onSelect(currentValue ? [...currentValue, option] : option);
        }
      } else {
        if (!isOptionChecked(option, value)) {
          onSelect(option);
        }
        setOpen(false);
      }
    };

    function getSelectedLabel(): string | undefined {
      if (!Array.isArray(value)) {
        return value && value.label && value.label;
      }
      return undefined;
    }

    const stateClassName = classnames(isOpen ? 'opened' : 'closed');
    const textVariant = isJournalPage ? 'h6' : 'small';
    const rootClassName = classnames(
      styles['advanced-select'],
      stateClassName,
      className,
      {
        active: currentValue && currentValue.length > 0,
        disabled,
      },
      isHintVisible && validators && validators.some((v) => !v.isValid) && 'error',
      labelPosition === 'top' && 'with-label',
      'plc-form-element',
      variant
    );

    const dropdownClassName = classnames('plc-dropdown', stateClassName);

    const selectedValueLabel = getSelectedLabel();

    function renderLabel(optionsRender?: Value | Value[]) {
      if (!optionsRender) {
        return <Text flavour="grey" i18nKey={placeHolderI18n} tag="span" variant={textVariant} />;
      }
      if (Array.isArray(optionsRender) && optionsRender.length > 0) {
        return (
          <Text
            flavour={labelFlavour}
            i18nKey={typeof label === 'string' ? label : undefined}
            i18nOptions={{
              count: optionsRender.length,
            }}
            tag="span"
            variant={textVariant}
          >
            {typeof label !== 'string' && label}
          </Text>
        );
      }
      if (optionsRender && !Array.isArray(optionsRender)) {
        return (
          <Text flavour={labelFlavour} tag="span" variant={textVariant}>
            {optionsRender.value} {required && ' *'}
          </Text>
        );
      }
      return (
        <Text flavour={labelFlavour} tag="span" variant={textVariant}>
          {label} {required && ' *'}
        </Text>
      );
    }

    return (
      <div className={rootClassName} ref={node} {...rest}>
        {labelPosition === 'top' && (
          <div className="plc-mb-s">
            <Label
              label={label}
              labelFlavour={disabled ? 'grey' : labelFlavour}
              labelI18n={labelI18n}
              required={required}
            />
          </div>
        )}
        <SecondaryButton
          className={classnames({ disabled })}
          disabled={disabled}
          onClick={() => setOpen(!isOpen)}
          ref={advancedSelectRef as RefObject<HTMLButtonElement> | null}
          style={{ boxShadow: isJournalPage ? '0 4px 2px -2px rgba(0, 0, 0, 0.2)' : '' }}
          textProps={{ variant: 'h6' }}
          type="button"
        >
          {labelPosition === 'inner' && multiple && renderLabel(value)}
          {!multiple && (
            <Text
              flavour={selectedOptionFlavour}
              i18nKey={selectedValueLabel ? undefined : placeHolderI18n}
              tag="p"
              variant={textVariant}
            >
              {selectedValueLabel}
            </Text>
          )}
          {!hideArrow && <ChevronDownIcon height={18} width={18} />}
        </SecondaryButton>
        <ul className={dropdownClassName}>
          {options &&
            options.map((o) => (
              <li key={o.value}>
                {multiple ? (
                  <Checkbox
                    checked={isOptionChecked(o, currentValue)}
                    disabled={!isOpen}
                    isDarkGreen={isJournalPage}
                    label={o.label ? o.label : o.value}
                    onCheck={() => onSelection(o)}
                  />
                ) : (
                  <Text onClick={() => onSelection(o)} variant={textVariant}>
                    {o.label ? o.label : o.value}
                  </Text>
                )}
              </li>
            ))}
        </ul>
        <FormHints isHintVisible={isHintVisible && !disabled} validators={validators} />
      </div>
    );
  }
);
